import React, { useEffect, useRef, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { GENERATE_MUTATION, QUICK_ADD_TASK_MUTATION } from '../query/gqlQueries'
import s from './FlowView.module.scss'
import MicIcon from '@mui/icons-material/Mic'
import StopIcon from '@mui/icons-material/Stop'
import CloseIcon from '@mui/icons-material/Close'

// Adding work to a project by saying it: speak (or type) what needs doing,
// the Mac Studio splits it into tasks, and nothing is made until you've
// checked each one — title, who, priority, hours, kind, due — and said yes.
// The new tasks wait in the flow's tray, ready to drag in.

const TYPES = ['Admin', 'Design', '3D', 'Coding', 'Learning', 'Photography', 'Video']
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent']
const PEOPLE = ['David', 'Selinay', 'Tomas']
const SpeechRecognition = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
// As a new task elsewhere: two working days ahead (three after 4pm).
function defaultDue() {
  const d = new Date()
  let left = d.getHours() >= 16 ? 3 : 2
  while (left > 0) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) left -= 1 }
  return iso(d)
}
const pick = (list, v, fallback) => list.find(x => x.toLowerCase() === String(v || '').toLowerCase()) || fallback

export default function FlowAddTasks({ project, me, existing = [], onClose, onMade }) {
  const client = useApolloClient()
  const [said, setSaid] = useState('')
  const [interim, setInterim] = useState('')
  const [listening, setListening] = useState(false)
  const [thinking, setThinking] = useState(false)
  const [drafts, setDrafts] = useState(null)   // the suggested tasks, to check
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const recRef = useRef(null)
  const meName = pick(PEOPLE, me, 'David')

  useEffect(() => () => { try { recRef.current?.stop() } catch { /* fine */ } }, [])

  const toggleMic = () => {
    if (!SpeechRecognition) { setError('Speaking needs Safari or Chrome — you can type it instead.'); return }
    if (listening) { recRef.current?.stop(); return }
    const rec = new SpeechRecognition()
    rec.lang = navigator.language || 'en-GB'
    rec.continuous = true
    rec.interimResults = true
    rec.onstart = () => { setListening(true); setError('') }
    rec.onend = () => { setListening(false); setInterim('') }
    rec.onerror = (e) => { if (e.error !== 'no-speech' && e.error !== 'aborted') setError(e.error === 'not-allowed' ? 'The microphone isn’t allowed for this site.' : 'Couldn’t hear that — try again.') }
    rec.onresult = (event) => {
      let finals = '', live = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i]
        if (r.isFinal) finals += r[0].transcript; else live += r[0].transcript
      }
      if (finals) setSaid(prev => (prev.trim() ? prev.trim() + ' ' : '') + finals.trim())
      setInterim(live)
    }
    recRef.current = rec
    try { rec.start() } catch { /* already running */ }
  }

  const makeSuggestions = async () => {
    try { recRef.current?.stop() } catch { /* fine */ }
    const text = said.trim()
    if (!text) return
    setThinking(true); setError('')
    try {
      const today = new Date()
      const prompt = `You turn what someone said into tasks for a small creative studio's project board (design, 3D, video, photography, code, admin).

Project: ${project}
Today: ${iso(today)} (${today.toLocaleDateString('en-GB', { weekday: 'long' })})
The person speaking: ${meName}
The team: ${PEOPLE.join(', ')}
Tasks already on the project (don't repeat these): ${existing.slice(0, 60).map(t => t.properties?.title).filter(Boolean).join('; ') || 'none'}

What they said:
"""${text}"""

Split it into separate, concrete tasks — one per piece of work they mentioned, no more. Keep their words where you can; a short title (under 70 characters) in the imperative. description: only detail they gave beyond the title (empty if none — never repeat the title). Don't invent work they didn't mention.
- assignee: someone from the team only if they named them, otherwise ${meName}
- priority: Low, Medium, High or Urgent — Medium unless they said it's urgent, important, or can wait
- hours: their estimate if they gave one, otherwise a sensible guess (0.5 to 16)
- type: one of ${TYPES.join(', ')}
- due: YYYY-MM-DD only if they said when (e.g. "by Friday"), otherwise null

Return ONLY JSON: {"tasks":[{"title":"","description":"","assignee":"","priority":"","hours":1,"type":"","due":null}]}`
      const { data } = await client.mutate({ mutation: GENERATE_MUTATION, variables: { prompt, maxTokens: 1500, json: true } })
      const out = String(data?.generate?.text || '')
      const a = out.indexOf('{'), b = out.lastIndexOf('}')
      let root = null
      try { root = JSON.parse(out.slice(a, b + 1)) } catch { root = null }
      const list = (root?.tasks || []).filter(t => t && String(t.title || '').trim())
      if (!list.length) throw new Error('No tasks came out of that — try saying it again, a little more plainly.')
      setDrafts(list.slice(0, 12).map((t, i) => ({
        key: i,
        keep: true,
        title: String(t.title).trim().slice(0, 120),
        description: String(t.description || '').trim(),
        assignee: pick(PEOPLE, t.assignee, meName),
        priority: pick(PRIORITIES, t.priority, 'Medium'),
        hours: Math.min(40, Math.max(0.5, Math.round((Number(t.hours) || 1) * 2) / 2)),
        type: pick(TYPES, t.type, 'Admin'),
        due: /^\d{4}-\d{2}-\d{2}$/.test(String(t.due || '')) ? t.due : defaultDue(),
      })))
    } catch (e) {
      setError(/530|Failed to fetch|Load failed/.test(e.message || '') ? 'The Mac Studio isn’t answering — it may be asleep.' : e.message)
    }
    setThinking(false)
  }

  const edit = (key, patch) => setDrafts(ds => ds.map(d => (d.key === key ? { ...d, ...patch } : d)))
  const chosen = (drafts || []).filter(d => d.keep && d.title.trim())

  const make = async () => {
    if (!chosen.length) return
    setSaving(true); setError('')
    let made = 0
    try {
      for (const d of chosen) {
        await client.mutate({
          mutation: QUICK_ADD_TASK_MUTATION,
          variables: {
            title: d.title.trim(), description: d.description, type: d.type, position: 'Backlog', priority: d.priority,
            assignee: d.assignee, timeBudget: Number(d.hours) || 0, timeSpent: 0, dueDate: `${d.due}T16:00:00`,
            username: me || meName, project,
          },
        })
        made += 1
        setDrafts(ds => ds.map(x => (x.key === d.key ? { ...x, keep: false, made: true } : x)))
      }
      onMade && onMade(made)
      onClose()
    } catch (e) {
      setError(`${made ? `Made ${made}, then: ` : ''}${e.message || 'That didn’t save.'}`)
      if (made) onMade && onMade(made)
    }
    setSaving(false)
  }

  return (
    <div className={s.sheetScrim} onClick={onClose}>
      <div className={s.sheet} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Add tasks">
        <div className={s.sheetHead}>
          <div>
            <div className={s.sheetTitle}>{drafts ? 'Are these right?' : 'Add tasks'}</div>
            <div className={s.sheetNote}>{drafts
              ? 'Check each one — change anything that’s off, untick what you don’t want. Nothing’s made until you say so.'
              : `Say what needs doing on ${project} — as many things as you like. They’ll be split into tasks for you to check.`}</div>
          </div>
          <button className={s.round} onClick={onClose} aria-label="Close"><CloseIcon /></button>
        </div>

        {!drafts && (
          <>
            <div className={s.sayBox}>
              <textarea value={said + (interim ? (said ? ' ' : '') + interim : '')} onChange={(e) => { setSaid(e.target.value); setInterim('') }}
                placeholder="e.g. “Selinay needs to shoot the new cork bags on Thursday, about three hours, then I'll edit them — and the shipping page is urgent.”"
                rows={5} readOnly={listening} />
              <button className={`${s.mic} ${listening ? s.micOn : ''}`} onClick={toggleMic} aria-label={listening ? 'Stop listening' : 'Speak'}>
                {listening ? <StopIcon /> : <MicIcon />}
              </button>
            </div>
            {listening && <div className={s.listening}><i />Listening… tap the button when you’re done.</div>}
            {error && <div className={s.error}>{error}</div>}
            <div className={s.sheetActions}>
              <button className={s.discard} onClick={onClose}>Cancel</button>
              <button className={s.useIt} onClick={makeSuggestions} disabled={!said.trim() || thinking}>{thinking ? 'Making tasks…' : 'Suggest tasks'}</button>
            </div>
          </>
        )}

        {drafts && (
          <>
            <div className={s.draftList}>
              {drafts.map(d => (
                <div key={d.key} className={`${s.draft} ${d.keep || d.made ? '' : s.draftOff}`}>
                  <button className={`${s.draftTick} ${d.keep || d.made ? s.draftTickOn : ''}`} disabled={saving || d.made}
                    onClick={() => edit(d.key, { keep: !d.keep })} aria-label={d.keep ? 'Leave this one out' : 'Keep this one'}>{d.keep || d.made ? '✓' : ''}</button>
                  <div className={s.draftBody}>
                    <input className={s.draftTitle} value={d.title} onChange={(e) => edit(d.key, { title: e.target.value })} disabled={!d.keep || saving} />
                    {d.description && <textarea className={s.draftDesc} value={d.description} rows={2} onChange={(e) => edit(d.key, { description: e.target.value })} disabled={!d.keep || saving} />}
                    <div className={s.draftFields}>
                      <select value={d.assignee} onChange={(e) => edit(d.key, { assignee: e.target.value })} disabled={!d.keep || saving} aria-label="Who">
                        {PEOPLE.map(p => <option key={p}>{p}</option>)}
                      </select>
                      <select className={s['pr_' + d.priority.toLowerCase()]} value={d.priority} onChange={(e) => edit(d.key, { priority: e.target.value })} disabled={!d.keep || saving} aria-label="Priority">
                        {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                      </select>
                      <label className={s.draftHours}><input type="number" min="0.5" step="0.5" value={d.hours} onChange={(e) => edit(d.key, { hours: e.target.value })} disabled={!d.keep || saving} aria-label="Hours" /> h</label>
                      <select value={d.type} onChange={(e) => edit(d.key, { type: e.target.value })} disabled={!d.keep || saving} aria-label="Kind of work">
                        {TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                      <label className={s.draftDue}>due <input type="date" value={d.due} onChange={(e) => edit(d.key, { due: e.target.value })} disabled={!d.keep || saving} /></label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {error && <div className={s.error}>{error}</div>}
            <div className={s.sheetActions}>
              <button className={s.discard} onClick={() => { setDrafts(null); setError('') }} disabled={saving}>← Say it again</button>
              <button className={s.useIt} onClick={make} disabled={!chosen.length || saving}>
                {saving ? 'Making…' : `Yes, make ${chosen.length} task${chosen.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
